import React, { useState } from 'react';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Star, TrendingUp, TrendingDown, Plus, Save, Flag, Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { mediaProperties, regions, channels, vendors } from '../data/buyingData';

const BuyingRepository = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    region: 'All Regions',
    channel: 'All Channels',
    vendor: 'All Vendors'
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [preset, setPreset] = useState('last_30');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const getPresetRange = (presetKey) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start = new Date(end);

    if (presetKey === 'last_7') {
      start.setDate(end.getDate() - 6);
    } else if (presetKey === 'last_30') {
      start.setDate(end.getDate() - 29);
    } else if (presetKey === 'this_month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (presetKey === 'last_month') {
      const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 24 * 60 * 60 * 1000);
      start = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { from: start, to: lastMonthEnd };
    }
    return { from: start, to: end };
  };

  const [dateRange, setDateRange] = useState(() => getPresetRange('last_30'));

  const handlePresetChange = (value) => {
    setPreset(value);
    const range = getPresetRange(value);
    setDateRange(range);
  };

  const rangeLabel = dateRange?.from && dateRange?.to
    ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    : 'Select date range';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getVarianceBadge = (variance) => {
    if (variance > 0) {
      return <Badge className="bg-red-100 text-red-800">+{variance.toFixed(1)}%</Badge>;
    } else {
      return <Badge className="bg-green-100 text-green-800">{variance.toFixed(1)}%</Badge>;
    }
  };

  const getAvailabilityBadge = (availability) => {
    const colors = {
      'High': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-red-100 text-red-800',
      'Seasonal': 'bg-purple-100 text-purple-800'
    };
    return <Badge className={colors[availability]}>{availability}</Badge>;
  };

  const filteredProperties = mediaProperties.filter(property => {
    const matchesSearch = 
      property.property.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilters = 
      (filters.region === 'All Regions' || property.region === filters.region) &&
      (filters.channel === 'All Channels' || property.channel === filters.channel) &&
      (filters.vendor === 'All Vendors' || property.vendor === filters.vendor);
    
    return matchesSearch && matchesFilters;
  });

  const sortedProperties = [...filteredProperties].sort((a, b) => {
    switch (sortBy) {
      case 'rate':
        return a.lastPurchasedRate - b.lastPurchasedRate;
      case 'variance':
        return a.variance - b.variance;
      case 'performance':
        return (b.performance.cpm || 0) - (a.performance.cpm || 0);
      default:
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
    }
  });

  const PropertyCard = ({ property }) => (
    <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
              {property.property}
            </CardTitle>
            <div className="flex items-center space-x-2 mb-2">
              <Badge className="bg-blue-100 text-blue-800">{property.channel}</Badge>
              <Badge className="bg-gray-100 text-gray-800">{property.region}</Badge>
              {property.preferredVendor && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
            </div>
            <p className="text-sm text-gray-600">{property.vendor}</p>
          </div>
          {getAvailabilityBadge(property.availability)}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4">
          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Last Rate</div>
              <div className="text-lg font-bold text-blue-900">
                {formatCurrency(property.lastPurchasedRate)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 font-medium">Benchmark</div>
              <div className="text-lg font-bold text-gray-900">
                {formatCurrency(property.benchmarkedRate)}
              </div>
            </div>
          </div>

          {/* Variance */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Price Variance:</span>
            <div className="flex items-center space-x-2">
              {property.variance > 0 ? 
                <TrendingUp className="h-4 w-4 text-red-500" /> :
                <TrendingDown className="h-4 w-4 text-green-500" />
              }
              {getVarianceBadge(property.variance)}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-2">Performance Metrics</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {Object.entries(property.performance).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="font-semibold text-blue-800">
                    {typeof value === 'number' ? value.toFixed(2) : value}
                  </div>
                  <div className="text-blue-600 uppercase">{key}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div>
            <div className="text-sm text-gray-600 mb-1">Target Audience</div>
            <div className="text-sm font-medium text-gray-900">{property.audienceReach}</div>
          </div>

          {/* Actions */}
          <div className="flex space-x-2 pt-2">
            <Button size="sm" className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Plus className="h-4 w-4 mr-1" />
              Add to Proposal
            </Button>
            <Button size="sm" variant="outline">
              <Save className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline">
              <Flag className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Buying Repository & Price Benchmarking
          </h1>
          <p className="text-gray-600">Media Properties, Rates & Performance Benchmarks</p>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 px-4">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {rangeLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3">
                  <Calendar
                    mode="range"
                    numberOfMonths={2}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range || { from: null, to: null });
                    }}
                    initialFocus
                  />
                  <div className="flex justify-end gap-2 pt-3">
                    <Button variant="ghost" size="sm" onClick={() => setIsCalendarOpen(false)}>Cancel</Button>
                    <Button size="sm" onClick={() => setIsCalendarOpen(false)}>Apply</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="min-w-[220px]">
              <Select value={preset} onValueChange={handlePresetChange}>
                <SelectTrigger aria-label="Date range preset">
                  <SelectValue placeholder="Select preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7">Last 7 days</SelectItem>
                  <SelectItem value="last_30">Last 30 days</SelectItem>
                  <SelectItem value="this_month">This month</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Search & Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search properties, vendors, categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={filters.region} onValueChange={(value) => setFilters({...filters, region: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.channel} onValueChange={(value) => setFilters({...filters, channel: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Channel" />
                </SelectTrigger>
                <SelectContent>
                  {channels.map(channel => (
                    <SelectItem key={channel} value={channel}>{channel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lastUpdated">Latest Updated</SelectItem>
                  <SelectItem value="rate">Price (Low to High)</SelectItem>
                  <SelectItem value="variance">Best Variance</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600">
                {sortedProperties.length} properties found
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  onClick={() => setViewMode('list')}
                >
                  List
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className={viewMode === 'grid' ? 
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 
          'space-y-4'
        }>
          {sortedProperties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>

        {/* Summary Stats */}
        {/* Enhanced Portfolio Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          {/* Historical Performance Archive */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Historical Performance Archive
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="font-bold text-blue-600">Q4 2024</div>
                    <div className="text-blue-700">15% Cost Reduction</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="font-bold text-green-600">Q3 2024</div>
                    <div className="text-green-700">ROI Improved 22%</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="font-bold text-purple-600">Q2 2024</div>
                    <div className="text-purple-700">New Vendors: 8</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">UAE - Digital Performance</span>
                    <Badge className="bg-green-100 text-green-800 text-xs">+18% YoY</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">Saudi - TV Optimization</span>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">-12% Cost</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">Kuwait - Outdoor Efficiency</span>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">Best ROI</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferred Partners & Savings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Preferred Partners & Savings Tracker
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Cumulative Savings vs Industry</h4>
                  <div className="text-3xl font-bold text-green-600 mb-1">{formatCurrency(450000)}</div>
                  <div className="text-sm text-green-700">Saved in 2025 • 23% below industry average</div>
                </div>
                
                <div className="space-y-2">
                  <h5 className="font-medium text-gray-900">Top Preferred Partners</h5>
                  <div className="space-y-2">
                    {[
                      { name: 'Google MENA', savings: 125000, rating: 4.8, deals: 15 },
                      { name: 'Meta MENA', savings: 89000, rating: 4.7, deals: 12 },
                      { name: 'MBC Group', savings: 67000, rating: 4.6, deals: 8 }
                    ].map((partner, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <div className="font-medium text-sm">{partner.name}</div>
                          <div className="text-xs text-gray-600">{partner.deals} deals • {partner.rating}★ rating</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-green-600">{formatCurrency(partner.savings)}</div>
                          <div className="text-xs text-gray-500">saved</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Portfolio Summary */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Portfolio Summary & Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{sortedProperties.length}</div>
                <div className="text-sm text-blue-700">Total Properties</div>
                <div className="text-xs text-gray-500 mt-1">Across {new Set(sortedProperties.map(p => p.region)).size} regions</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {sortedProperties.filter(p => p.variance < 0).length}
                </div>
                <div className="text-sm text-green-700">Below Benchmark</div>
                <div className="text-xs text-gray-500 mt-1">Cost efficient deals</div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {sortedProperties.filter(p => p.preferredVendor).length}
                </div>
                <div className="text-sm text-yellow-700">Preferred Vendors</div>
                <div className="text-xs text-gray-500 mt-1">Proven ROI partners</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(
                    sortedProperties.reduce((acc, p) => acc + (p.benchmarkedRate - p.lastPurchasedRate), 0)
                  )}
                </div>
                <div className="text-sm text-purple-700">YTD Savings</div>
                <div className="text-xs text-gray-500 mt-1">vs benchmarks</div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">4.2x</div>
                <div className="text-sm text-red-700">Avg Portfolio ROI</div>
                <div className="text-xs text-gray-500 mt-1">Weighted average</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <AIInsightsChatbot />
    </div>
  );
};

export default BuyingRepository;